import React, { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useSessionBootstrap } from "../hooks/useSessionBootstrap";
import { useSession } from "../context/SessionContext";
import { destinoAposLogin } from "../lib/checkoutIntent";
import { LoadingScreen } from "./LoadingScreen";
import { PATHS } from "./paths";
import { showError } from "../lib/feedback";
import { LuTriangleAlert } from "react-icons/lu";
import { Button } from "../components/ui";
import { StatusCard } from "../components/patterns/StatusCard";
import { t } from "../i18n";

export const AuthCallbackPage: React.FC = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const { billing } = useSession();
  const { status, doctor } = useSessionBootstrap();
  const hasNavigated = useRef(false);

  // Dispara o login (Auth0 Universal Login) quando não autenticado
  useEffect(() => {
    if (isLoading || isAuthenticated) return;

    loginWithRedirect({
      authorizationParams: {
        audience: "https://dev-yudz81r54mtavkzj.us.auth0.com/api/v2/",
        scope: "read:current_user",
      },
    }).catch(showError);
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  // Reage ao resultado do bootstrap de sessão
  useEffect(() => {
    if (hasNavigated.current) return;

    switch (status) {
      case "ready": {
        hasNavigated.current = true;
        const returnTo = sessionStorage.getItem("returnTo");
        sessionStorage.removeItem("returnTo");
        // Se o returnTo ainda aponta para /assinar (fluxo da landing) e a
        // assinatura já está ativa, entra no app — senão o checkout 409
        // prende o médico na tela "gerenciar assinatura".
        navigate(destinoAposLogin(returnTo, billing), { replace: true });
        break;
      }
      case "needs-onboarding":
        hasNavigated.current = true;
        navigate(PATHS.onboarding, { state: { doctor }, replace: true });
        break;
      // "loading" e "error" não navegam — "error" já mostrou o toast
      // adequado dentro do hook de bootstrap.
      default:
        break;
    }
  }, [status, doctor, billing, navigate]);

  // Backend fora do ar ou perfil inacessível: sem isto a tela ficava no carregamento para sempre.
  if (status === "error") {
    return (
      <StatusCard
        tone="danger"
        icon={<LuTriangleAlert size={28} />}
        title={t("session.loadFailedTitle")}
        actions={<Button variant="primary" onClick={() => window.location.reload()}>{t("common.retry")}</Button>}
      >
        <p>{t("session.loadFailedBody")}</p>
      </StatusCard>
    );
  }

  return <LoadingScreen />;
};
